import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { RefreshCw,Search, Eye, ChevronRight, Package, CheckCircle, Clock, XCircle, AlertCircle, Briefcase,Tag, Layers, Box, FileText, Workflow, ChevronDown, Image as ImageIcon, GitBranch, Maximize2,  Trash2, AlertTriangle, Loader2, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import config from '@/config';

import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  Position,
  Handle,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

// Softbox/Lightbox Component
function Softbox({ isOpen, onClose, imageUrl, title }) {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-90 p-4 animate-fadeIn"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 bg-white bg-opacity-10 hover:bg-opacity-20 rounded-full transition-colors z-10"
      >
        <X className="w-6 h-6 text-white" />
      </button>
      
      <div className="max-w-[90vw] max-h-[90vh] flex flex-col items-center gap-4">
        {title && (
          <div className="text-white text-lg font-medium bg-black bg-opacity-50 px-4 py-2 rounded-lg">
            {title}
          </div>
        )}
        <img
          src={imageUrl}
          alt={title || 'Image'}
          className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        />
        <div className="text-white text-sm bg-black bg-opacity-50 px-4 py-2 rounded-lg">
          Click anywhere to close
        </div>
      </div>
    </div>
  );
}

// Node Detail Modal Component
function WorkflowNodeDetailModal({ isOpen, onClose, nodeId }) {
  const { authenticatedFetch } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [nodeDetail, setNodeDetail] = useState(null);
  const [softboxOpen, setSoftboxOpen] = useState(false);
  const [softboxImage, setSoftboxImage] = useState(null);

  useEffect(() => {
    if (!isOpen || !nodeId) return;

    const fetchNodeDetail = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await authenticatedFetch(
          `${config.BACKEND_API_ADDRESS}/workflows/workflow-nodes/${nodeId}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setNodeDetail(data);
      } catch (err) {
        console.error('Error fetching node detail:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchNodeDetail();
  }, [isOpen, nodeId, authenticatedFetch]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'running': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'failed': return 'bg-red-100 text-red-800 border-red-200';
      case 'needs_update': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'running': return <RefreshCw className="w-4 h-4 animate-spin" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'failed': return <XCircle className="w-4 h-4" />;
      case 'needs_update': return <AlertCircle className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  // Component to render an image with overlays (bbox or mask)
  const ImageWithOverlay = ({ imageUrl, labelData, title, compact = false }) => {
    const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
    const imageRef = useRef(null);

    useEffect(() => {
      if (imageRef.current) {
        const updateDimensions = () => {
          setImageDimensions({
            width: imageRef.current.width,
            height: imageRef.current.height
          });
        };
        
        if (imageRef.current.complete) {
          updateDimensions();
        } else {
          imageRef.current.onload = updateDimensions;
        }
      }
    }, [imageUrl]);

    const renderBoundingBox = (bboxData) => {
      if (!bboxData || !imageDimensions.width) return null;

      const { x1_percent, y1_percent, x2_percent, y2_percent } = bboxData;
      
      const left = (x1_percent / 100) * imageDimensions.width;
      const top = (y1_percent / 100) * imageDimensions.height;
      const width = ((x2_percent - x1_percent) / 100) * imageDimensions.width;
      const height = ((y2_percent - y1_percent) / 100) * imageDimensions.height;

      return (
        <div
          className="absolute border-2 border-green-500 bg-green-500 bg-opacity-20 pointer-events-none"
          style={{
            left: `${left}px`,
            top: `${top}px`,
            width: `${width}px`,
            height: `${height}px`,
          }}
        >
          {!compact && (
            <div className="absolute -top-6 left-0 bg-green-500 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
              {labelData.label_kind || 'Label'}
              {labelData.tag && ` - ${labelData.tag}`}
            </div>
          )}
        </div>
      );
    };

    const renderSegmentationMask = (segmentationData) => {
      if (!segmentationData?.mask || !imageDimensions.width) return null;

      return (
        <img
          src={segmentationData.mask}
          alt="Segmentation mask"
          className="absolute inset-0 w-full h-full object-cover opacity-50 pointer-events-none"
          style={{ mixBlendMode: 'multiply' }}
        />
      );
    };

    return (
      <div className="relative inline-block group">
        <img
          ref={imageRef}
          src={imageUrl}
          alt={title || "Output"}
          className={`${compact ? 'max-w-full' : 'max-w-md'} rounded-lg border-2 border-gray-300 shadow-sm hover:shadow-md transition-all cursor-pointer hover:border-blue-500`}
          onClick={(e) => {
            e.stopPropagation();
            setSoftboxImage({ url: imageUrl, title: title || 'Image', labelData });
            setSoftboxOpen(true);
          }}
        />
        
        {/* Render bounding box if available */}
        {labelData?.bbox_data && renderBoundingBox(labelData.bbox_data)}
        
        {/* Render segmentation mask if available */}
        {labelData?.segmentation_data && renderSegmentationMask(labelData.segmentation_data)}
        
        {/* Hover effect */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <div className="bg-black bg-opacity-60 rounded-full p-3">
            <Eye className="w-6 h-6 text-white" />
          </div>
        </div>
        
        {/* Label info badge */}
        {labelData && !compact && (
          <div className="absolute bottom-2 left-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
            {labelData.label_kind || 'Label'}
            {labelData.confidence && ` (${(labelData.confidence * 100).toFixed(1)}%)`}
          </div>
        )}
      </div>
    );
  };

  // Component to render label data in two-column layout
  const LabelDataDisplay = ({ labelData, index }) => {
    const imageUrl = labelData.image_url;
    const hasBbox = labelData.bbox_data;
    const hasSegmentation = labelData.segmentation_data;
    
    return (
      <div className="bg-white rounded-lg border-2 border-blue-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Tag className="w-5 h-5" />
              <h4 className="font-semibold">
                Label {index !== undefined ? `#${index + 1}` : ''}
              </h4>
            </div>
            <div className="flex items-center gap-2">
              {hasBbox && (
                <span className="bg-green-500 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                  <Box className="w-3 h-3" />
                  BBox
                </span>
              )}
              {hasSegmentation && (
                <span className="bg-purple-500 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                  <Layers className="w-3 h-3" />
                  Mask
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">
          {/* LEFT COLUMN: Visual Data */}
          <div className="space-y-3">
            <div className="bg-gray-50 rounded-lg p-3">
              <h5 className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide flex items-center gap-1">
                <ImageIcon className="w-4 h-4" />
                Visual Data
              </h5>
              
              {imageUrl ? (
                <ImageWithOverlay 
                  imageUrl={imageUrl} 
                  labelData={labelData}
                  title={`Label ${labelData.label_kind || ''}`}
                  compact={true}
                />
              ) : (
                <div className="bg-gray-100 rounded-lg p-8 text-center">
                  <ImageIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No image available</p>
                </div>
              )}
            </div>

            {/* Bbox Data */}
            {hasBbox && (
              <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                <h5 className="text-xs font-semibold text-green-800 mb-2 uppercase tracking-wide flex items-center gap-1">
                  <Box className="w-4 h-4" />
                  Bounding Box
                </h5>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-white rounded p-2">
                    <span className="text-gray-500">X1:</span>
                    <span className="ml-1 font-mono text-gray-900">{labelData.bbox_data.x1_percent.toFixed(2)}%</span>
                  </div>
                  <div className="bg-white rounded p-2">
                    <span className="text-gray-500">Y1:</span>
                    <span className="ml-1 font-mono text-gray-900">{labelData.bbox_data.y1_percent.toFixed(2)}%</span>
                  </div>
                  <div className="bg-white rounded p-2">
                    <span className="text-gray-500">X2:</span>
                    <span className="ml-1 font-mono text-gray-900">{labelData.bbox_data.x2_percent.toFixed(2)}%</span>
                  </div>
                  <div className="bg-white rounded p-2">
                    <span className="text-gray-500">Y2:</span>
                    <span className="ml-1 font-mono text-gray-900">{labelData.bbox_data.y2_percent.toFixed(2)}%</span>
                  </div>
                </div>
              </div>
            )}

            {/* Segmentation Data */}
            {hasSegmentation && (
              <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                <h5 className="text-xs font-semibold text-purple-800 mb-2 uppercase tracking-wide flex items-center gap-1">
                  <Layers className="w-4 h-4" />
                  Segmentation Mask
                </h5>
                <div className="bg-white rounded p-2">
                  <p className="text-xs text-gray-600">Mask data available</p>
                  {labelData.segmentation_data.mask && (
                    <p className="text-xs text-gray-500 mt-1 truncate">
                      {labelData.segmentation_data.mask.substring(0, 50)}...
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT COLUMN: Label Information */}
          <div className="space-y-3">
            <div className="bg-gray-50 rounded-lg p-3">
              <h5 className="text-xs font-semibold text-gray-600 mb-3 uppercase tracking-wide flex items-center gap-1">
                <FileText className="w-4 h-4" />
                Label Information
              </h5>
              
              <div className="space-y-2">
                {/* Label ID */}
                <div className="bg-white rounded-lg p-2">
                  <p className="text-xs font-medium text-gray-500 mb-1">Label ID</p>
                  <p className="text-xs font-mono text-gray-900 break-all">{labelData.id}</p>
                </div>

                {/* Label Kind */}
                {labelData.label_kind && (
                  <div className="bg-white rounded-lg p-2">
                    <p className="text-xs font-medium text-gray-500 mb-1">Kind</p>
                    <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-semibold">
                      {labelData.label_kind}
                    </span>
                  </div>
                )}

                {/* Tag */}
                {labelData.tag && (
                  <div className="bg-white rounded-lg p-2">
                    <p className="text-xs font-medium text-gray-500 mb-1">Tag</p>
                    <span className="inline-block bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs font-semibold">
                      {labelData.tag}
                    </span>
                  </div>
                )}

                {/* Confidence */}
                {labelData.confidence !== null && labelData.confidence !== undefined && (
                  <div className="bg-white rounded-lg p-2">
                    <p className="text-xs font-medium text-gray-500 mb-1">Confidence</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div 
                          className="bg-green-500 h-full transition-all"
                          style={{ width: `${labelData.confidence * 100}%` }}
                        />
                      </div>
                      <span className="text-xs font-mono text-gray-900 min-w-[3rem] text-right">
                        {(labelData.confidence * 100).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                )}

                {/* Label Origin */}
                {labelData.label_origin && (
                  <div className="bg-white rounded-lg p-2">
                    <p className="text-xs font-medium text-gray-500 mb-1">Origin</p>
                    <p className="text-xs text-gray-900">{labelData.label_origin}</p>
                  </div>
                )}

                {/* Image ID */}
                {labelData.image_id && (
                  <div className="bg-white rounded-lg p-2">
                    <p className="text-xs font-medium text-gray-500 mb-1">Image ID</p>
                    <p className="text-xs font-mono text-gray-900 break-all">{labelData.image_id}</p>
                  </div>
                )}

                {/* Annotator ID */}
                {labelData.annotator_id && (
                  <div className="bg-white rounded-lg p-2">
                    <p className="text-xs font-medium text-gray-500 mb-1">Annotator ID</p>
                    <p className="text-xs font-mono text-gray-900 break-all">{labelData.annotator_id}</p>
                  </div>
                )}

                {/* Comment */}
                {labelData.comment && (
                  <div className="bg-white rounded-lg p-2">
                    <p className="text-xs font-medium text-gray-500 mb-1">Comment</p>
                    <p className="text-xs text-gray-900 italic">{labelData.comment}</p>
                  </div>
                )}

                {/* Timestamps */}
                <div className="bg-white rounded-lg p-2">
                  <p className="text-xs font-medium text-gray-500 mb-2">Timestamps</p>
                  <div className="space-y-1">
                    {labelData.created_at && (
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <Clock className="w-3 h-3" />
                        <span className="text-gray-500">Created:</span>
                        <span className="font-mono">{new Date(labelData.created_at).toLocaleString()}</span>
                      </div>
                    )}
                    {labelData.updated_at && (
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <RefreshCw className="w-3 h-3" />
                        <span className="text-gray-500">Updated:</span>
                        <span className="font-mono">{new Date(labelData.updated_at).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Recursively render output data with image support and collapsible sections
  const CollapsibleSection = ({ title, children, defaultOpen = true, isImageUrl = false, isLabelData = false, depth = 0 }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    
    return (
      <div className={
        isLabelData ? 'bg-blue-50 rounded-lg p-4 border border-blue-200' :
        isImageUrl ? 'bg-blue-50 rounded-lg p-4' : 
        'bg-gray-50 rounded-lg p-3'
      }>
        <div
          className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isLabelData && <Tag className="w-4 h-4 text-blue-600 flex-shrink-0" />}
          {isImageUrl && !isLabelData && <ImageIcon className="w-4 h-4 text-blue-600 flex-shrink-0" />}
          <ChevronRight className={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ${isOpen ? 'rotate-90' : ''}`} />
          <div className={`text-sm font-semibold ${isLabelData || isImageUrl ? 'text-blue-900' : 'text-gray-700'} flex-1`}>
            {title}
          </div>
        </div>
        {isOpen && (
          <div className="mt-2 ml-6">
            {children}
          </div>
        )}
      </div>
    );
  };

  const renderOutputData = (data, depth = 0, parentKey = '') => {
    if (data === null || data === undefined) {
      return <span className="text-gray-400 italic">null</span>;
    }

    if (typeof data === 'string' || typeof data === 'number' || typeof data === 'boolean') {
      // Check if it's an image URL
      if (typeof data === 'string' && data.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
        return <ImageWithOverlay imageUrl={data} title={parentKey || 'Image'} />;
      }
      return <span className="text-gray-900">{String(data)}</span>;
    }

    if (Array.isArray(data)) {
      // Check if this is an array of label data
      const isLabelArray = data.length > 0 && data[0]?.label_kind !== undefined;
      
      if (isLabelArray) {
        return (
          <div className="space-y-4">
            {data.map((labelData, index) => (
              <LabelDataDisplay key={labelData.id || index} labelData={labelData} index={index} />
            ))}
          </div>
        );
      }
      
      return (
        <div className={`space-y-2 ${depth > 0 ? 'ml-4 pl-4 border-l-2 border-gray-200' : ''}`}>
          {data.map((item, index) => (
            <CollapsibleSection
              key={index}
              title={`Item ${index + 1}`}
              defaultOpen={depth === 0}
              depth={depth}
            >
              {renderOutputData(item, depth + 1, `${parentKey}[${index}]`)}
            </CollapsibleSection>
          ))}
        </div>
      );
    }

    if (typeof data === 'object') {
      // Check if this is a single label data object
      const isLabelData = data.label_kind !== undefined;
      
      if (isLabelData) {
        return <LabelDataDisplay labelData={data} />;
      }
      
      return (
        <div className={`space-y-3 ${depth > 0 ? 'ml-4 pl-4 border-l-2 border-gray-200' : ''}`}>
          {Object.entries(data).map(([key, value]) => {
            // Check if this is an image URL key
            const isImageUrl = key.endsWith('_url') && key.includes('image');
            
            // Check if this is label data
            const isLabelDataKey = key.endsWith('_data') && (key.includes('label') || key === 'label_data');
            
            // Check if value is complex (object or array)
            const isComplex = typeof value === 'object' && value !== null && !Array.isArray(value) && !(typeof value === 'string' && value.match(/\.(jpg|jpeg|png|gif|webp)$/i));
            
            if (isComplex || Array.isArray(value)) {
              return (
                <CollapsibleSection
                  key={key}
                  title={key}
                  defaultOpen={depth === 0}
                  isImageUrl={isImageUrl}
                  isLabelData={isLabelDataKey}
                  depth={depth}
                >
                  {renderOutputData(value, depth + 1, key)}
                </CollapsibleSection>
              );
            }
            
            return (
              <div key={key} className={isImageUrl ? 'bg-blue-50 rounded-lg p-4' : 'bg-gray-50 rounded-lg p-3'}>
                <div className="flex items-start gap-3">
                  {isImageUrl && <ImageIcon className="w-4 h-4 text-blue-600 mt-1 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-semibold mb-2 ${isImageUrl ? 'text-blue-900' : 'text-gray-700'}`}>
                      {key}
                    </div>
                    <div className="text-sm">
                      {renderOutputData(value, depth + 1, key)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    return <span className="text-gray-900">{String(data)}</span>;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <GitBranch className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold">Workflow Node Details</h2>
              {nodeDetail && (
                <p className="text-purple-100 text-sm mt-1">
                  {nodeDetail.workflow_item_name || 'Node'}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-4">
                <RefreshCw className="w-12 h-12 text-indigo-600 animate-spin" />
                <p className="text-gray-600 font-medium">Loading node details...</p>
              </div>
            </div>
          ) : error ? (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">Error loading node details</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          ) : nodeDetail ? (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-xs font-medium text-gray-500 mb-1">Node ID</p>
                      <p className="text-sm font-mono text-gray-900 break-all">{nodeDetail.id}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-xs font-medium text-gray-500 mb-1">Workflow Item ID</p>
                      <p className="text-sm font-mono text-gray-900 break-all">{nodeDetail.workflow_item_id}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-xs font-medium text-gray-500 mb-1">Status</p>
                      <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(nodeDetail.status)}`}>
                        {getStatusIcon(nodeDetail.status)}
                        {nodeDetail.status}
                      </span>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-xs font-medium text-gray-500 mb-1">Version</p>
                      <p className="text-sm font-semibold text-gray-900">v{nodeDetail.version}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Output Data */}
              {nodeDetail.output_data && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <Maximize2 className="w-5 h-5 text-indigo-600" />
                      Output Data
                    </h3>
                  </div>
                  <div className="p-6">
                    {renderOutputData(nodeDetail.output_data)}
                  </div>
                </div>
              )}

              {/* No Output Data */}
              {!nodeDetail.output_data && (
                <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
                  <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium">No output data available</p>
                  <p className="text-sm text-gray-500 mt-1">This node hasn't produced any output yet</p>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {/* Softbox for images */}
      <Softbox
        isOpen={softboxOpen}
        onClose={() => setSoftboxOpen(false)}
        imageUrl={softboxImage?.url}
        title={softboxImage?.title}
        labelData={softboxImage?.labelData}
      />
    </div>
  );
}

// Custom Node Component for ReactFlow
const WorkflowNodeViz = ({ data }) => {
  const isCompleted = data.status === 'completed';
  const isRunning = data.status === 'running';
  const needsUpdate = data.status === 'needs_update';
  const isFailed = data.status === 'failed';

  const bgColor = isCompleted ? 'bg-green-50' :
                  isRunning ? 'bg-blue-50' :
                  needsUpdate ? 'bg-yellow-50' :
                  isFailed ? 'bg-red-50' :
                  'bg-gray-50';

  const borderColor = isCompleted ? 'border-green-500' :
                      isRunning ? 'border-blue-500' :
                      needsUpdate ? 'border-yellow-500' :
                      isFailed ? 'border-red-500' :
                      'border-gray-400';

  const statusColor = isCompleted ? 'bg-green-500 text-green-50' :
                      isRunning ? 'bg-blue-500 text-blue-50' :
                      needsUpdate ? 'bg-yellow-500 text-yellow-50' :
                      isFailed ? 'bg-red-500 text-red-50' :
                      'bg-gray-500 text-gray-50';

  return (
    <div 
      className={`${bgColor} rounded-lg shadow-lg px-4 py-3 border-2 ${borderColor} min-w-[180px] relative hover:shadow-xl transition-all cursor-pointer`}
      onClick={() => data.onNodeClick && data.onNodeClick(data.nodeId)}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={{
          width: '10px',
          height: '10px',
          backgroundColor: 'white',
          border: `2px solid ${isFailed ? '#ef4444' : '#3b82f6'}`,
        }}
      />

      <div className="space-y-1">
        <div className="flex items-start justify-between gap-2">
          <h4 className="font-bold text-xs text-gray-800 leading-tight">{data.label}</h4>
          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${statusColor} whitespace-nowrap`}>
            {data.status}
          </span>
        </div>

        <div className="text-xs text-gray-600">
          <div className="flex justify-between">
            <span className="font-medium">v{data.version}</span>
          </div>
        </div>
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          width: '10px',
          height: '10px',
          backgroundColor: 'white',
          border: `2px solid ${isFailed ? '#ef4444' : '#3b82f6'}`,
        }}
      />
    </div>
  );
};

function GlassesFrameDetailModal({ isOpen, onClose, frameId }) {
  const { authenticatedFetch } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [frameDetail, setFrameDetail] = useState(null);
  const [expandedJobs, setExpandedJobs] = useState(new Set());
  const [expandedWorkflows, setExpandedWorkflows] = useState(new Set());
  const [selectedWorkflowForViz, setSelectedWorkflowForViz] = useState(null);
  const [workflowDependencies, setWorkflowDependencies] = useState({});
  
  // Node detail modal state
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [nodeModalOpen, setNodeModalOpen] = useState(false);
  
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const nodeTypes = useMemo(() => ({ workflowNode: WorkflowNodeViz }), []);

  // Handle node click
  const handleNodeClick = useCallback((nodeId) => {
    setSelectedNodeId(nodeId);
    setNodeModalOpen(true);
  }, []);

  useEffect(() => {
    if (!isOpen || !frameId) return;

    const fetchDetail = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await authenticatedFetch(
          `${config.BACKEND_API_ADDRESS}/glasses-frames/${frameId}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setFrameDetail(data);
        
        // Auto-expand all jobs and workflows by default
        if (data.jobs) {
          const jobIds = new Set(data.jobs.map(j => j.id));
          setExpandedJobs(jobIds);
          
          const workflowIds = new Set();
          data.jobs.forEach(job => {
            if (job.workflows) {
              job.workflows.forEach(wf => workflowIds.add(wf.id));
            }
          });
          setExpandedWorkflows(workflowIds);

          // Auto-select first workflow for visualization
          if (data.jobs.length > 0 && data.jobs[0].workflows && data.jobs[0].workflows.length > 0) {
            setSelectedWorkflowForViz(data.jobs[0].workflows[0].id);
          }

          // Fetch dependencies for all workflows
          await fetchAllWorkflowDependencies(data.jobs);
        }
      } catch (err) {
        console.error('Error fetching frame detail:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [isOpen, frameId, authenticatedFetch]);

  const fetchAllWorkflowDependencies = async (jobs) => {
    const depsMap = {};
    
    for (const job of jobs) {
      if (job.workflows) {
        for (const workflow of job.workflows) {
          if (workflow.nodes) {
            const allDeps = [];
            
            for (const node of workflow.nodes) {
              try {
                const response = await authenticatedFetch(
                  `${config.BACKEND_API_ADDRESS}/workflows/workflow-node-dependencies/${node.id}`
                );
                
                if (response.ok) {
                  const data = await response.json();
                  if (data.dependencies) {
                    allDeps.push(...data.dependencies);
                  }
                }
              } catch (error) {
                console.error(`Error fetching dependencies for node ${node.id}:`, error);
              }
            }
            
            depsMap[workflow.id] = allDeps;
          }
        }
      }
    }
    
    setWorkflowDependencies(depsMap);
  };

  // Build ReactFlow visualization for selected workflow
  useEffect(() => {
    if (selectedWorkflowForViz && frameDetail) {
      buildWorkflowVisualization();
    }
  }, [selectedWorkflowForViz, workflowDependencies, frameDetail]);

  const buildWorkflowVisualization = () => {
    if (!selectedWorkflowForViz || !frameDetail) return;

    // Find the selected workflow
    let selectedWorkflow = null;
    let workflowNodes = [];
    
    for (const job of frameDetail.jobs || []) {
      for (const workflow of job.workflows || []) {
        if (workflow.id === selectedWorkflowForViz) {
          selectedWorkflow = workflow;
          workflowNodes = workflow.nodes || [];
          break;
        }
      }
      if (selectedWorkflow) break;
    }

    if (!selectedWorkflow || workflowNodes.length === 0) {
      setNodes([]);
      setEdges([]);
      return;
    }

    const dependencies = workflowDependencies[selectedWorkflowForViz] || [];
    
    // Build dependency graph
    const graph = {};
    workflowNodes.forEach(node => {
      graph[node.id] = {
        node,
        dependencies: [],
        dependents: []
      };
    });

    dependencies.forEach(dep => {
      if (graph[dep.node_id] && graph[dep.depends_on_node_id]) {
        graph[dep.node_id].dependencies.push(dep.depends_on_node_id);
        graph[dep.depends_on_node_id].dependents.push(dep.node_id);
      }
    });

    // Calculate levels (topological sort)
    const levels = {};
    const calculateLevel = (nodeId, visitedInPath = new Set()) => {
      if (visitedInPath.has(nodeId)) return 0;
      if (nodeId in levels) return levels[nodeId];
      
      visitedInPath.add(nodeId);
      const nodeData = graph[nodeId];
      
      if (!nodeData || nodeData.dependencies.length === 0) {
        levels[nodeId] = 0;
      } else {
        let maxDepLevel = -1;
        nodeData.dependencies.forEach(depId => {
          const depLevel = calculateLevel(depId, new Set(visitedInPath));
          maxDepLevel = Math.max(maxDepLevel, depLevel);
        });
        levels[nodeId] = maxDepLevel + 1;
      }
      
      visitedInPath.delete(nodeId);
      return levels[nodeId];
    };

    Object.keys(graph).forEach(nodeId => calculateLevel(nodeId));

    // Convert to level array
    const levelArray = [];
    Object.entries(levels).forEach(([nodeId, level]) => {
      if (!levelArray[level]) levelArray[level] = [];
      levelArray[level].push(nodeId);
    });

    // Create ReactFlow nodes
    const flowNodes = [];
    const nodeSpacingX = 250;
    const nodeSpacingY = 150;
    const startY = 50;

    levelArray.forEach((level, levelIndex) => {
      const nodesInLevel = level.length;
      const startX = (800 - (nodesInLevel * nodeSpacingX)) / 2;
      
      level.forEach((nodeId, index) => {
        const nodeData = workflowNodes.find(n => n.id === nodeId);
        if (!nodeData) return;

        flowNodes.push({
          id: nodeId,
          type: 'workflowNode',
          position: {
            x: startX + index * nodeSpacingX,
            y: startY + levelIndex * nodeSpacingY
          },
          data: {
            label: nodeData.workflow_item_name || 'Unknown',
            status: nodeData.status,
            version: nodeData.version,
            nodeId: nodeId,
            onNodeClick: handleNodeClick
          },
          sourcePosition: Position.Bottom,
          targetPosition: Position.Top,
        });
      });
    });

    // Create ReactFlow edges
    const flowEdges = [];
    dependencies.forEach(dep => {
      const sourceNode = workflowNodes.find(n => n.id === dep.depends_on_node_id);
      const targetNode = workflowNodes.find(n => n.id === dep.node_id);
      
      if (!sourceNode || !targetNode) return;

      const edgeColor = 
        targetNode.status === 'completed' ? '#10b981' :
        targetNode.status === 'failed' ? '#ef4444' :
        targetNode.status === 'running' ? '#3b82f6' :
        targetNode.status === 'needs_update' ? '#f59e0b' :
        '#6b7280';

      flowEdges.push({
        id: `e-${dep.depends_on_node_id}-${dep.node_id}`,
        source: dep.depends_on_node_id,
        target: dep.node_id,
        type: 'smoothstep',
        animated: targetNode.status === 'running',
        style: { stroke: edgeColor, strokeWidth: 2 },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: edgeColor,
        },
        label: `v${dep.consumed_version}`,
        labelStyle: { 
          fill: edgeColor, 
          fontWeight: 600,
          fontSize: 9
        },
        labelBgStyle: { 
          fill: 'white',
          fillOpacity: 0.8
        },
      });
    });

    setNodes(flowNodes);
    setEdges(flowEdges);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'running': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'failed': return 'bg-red-100 text-red-800 border-red-200';
      case 'needs_update': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'running': return <RefreshCw className="w-4 h-4 animate-spin" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'failed': return <XCircle className="w-4 h-4" />;
      case 'needs_update': return <AlertCircle className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  const calculateProgress = (nodes) => {
    if (!nodes || nodes.length === 0) return { completed: 0, total: 0, percentage: 0 };
    const completed = nodes.filter(n => n.status === 'completed').length;
    const total = nodes.length;
    return { completed, total, percentage: Math.round((completed / total) * 100) };
  };

  const getOverallJobProgress = (job) => {
    if (!job.workflows || job.workflows.length === 0) return { completed: 0, total: 0, percentage: 0 };
    
    let totalNodes = 0;
    let completedNodes = 0;
    
    job.workflows.forEach(workflow => {
      if (workflow.nodes) {
        totalNodes += workflow.nodes.length;
        completedNodes += workflow.nodes.filter(n => n.status === 'completed').length;
      }
    });
    
    return {
      completed: completedNodes,
      total: totalNodes,
      percentage: totalNodes > 0 ? Math.round((completedNodes / totalNodes) * 100) : 0
    };
  };

  const toggleJob = (jobId) => {
    setExpandedJobs(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) {
        next.delete(jobId);
      } else {
        next.add(jobId);
      }
      return next;
    });
  };

  const toggleWorkflow = (workflowId) => {
    setExpandedWorkflows(prev => {
      const next = new Set(prev);
      if (next.has(workflowId)) {
        next.delete(workflowId);
      } else {
        next.add(workflowId);
      }
      return next;
    });
  };

  const ProgressBar = ({ completed, total, status }) => {
    const percentage = total > 0 ? (completed / total) * 100 : 0;
    const bgColor = status === 'completed' ? 'bg-green-500' :
                    status === 'running' ? 'bg-blue-500' :
                    status === 'failed' ? 'bg-red-500' :
                    'bg-gray-400';

    return (
      <div className="flex items-center gap-3 flex-1">
        <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
          <div
            className={`h-full ${bgColor} transition-all duration-500 ease-out`}
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="text-sm font-medium text-gray-700 min-w-[80px] text-right">
          {completed}/{total} ({Math.round(percentage)}%)
        </span>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-[95vw] max-h-[95vh] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white p-6 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Glasses Frame Details</h2>
              {frameDetail && (
                <p className="text-indigo-100 mt-1">
                  {frameDetail.brand && <span className="font-medium">{frameDetail.brand}</span>}
                  {frameDetail.brand && frameDetail.name && ' • '}
                  {frameDetail.name}
                  {frameDetail.version && <span className="ml-2 text-sm opacity-90">v{frameDetail.version}</span>}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex flex-col items-center gap-4">
                  <RefreshCw className="w-12 h-12 text-indigo-600 animate-spin" />
                  <p className="text-gray-600 font-medium">Loading details...</p>
                </div>
              </div>
            ) : error ? (
              <div className="p-4 m-6 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-800">Error loading details</p>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            ) : frameDetail ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
                {/* Left Column - Frame Info and Jobs */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Frame Info Card */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <Package className="w-5 h-5 text-indigo-600" />
                        Frame Information
                      </h3>
                    </div>
                    <div className="p-6">
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs font-medium text-gray-500 mb-1">Brand</p>
                          <p className="text-sm font-semibold text-gray-900">{frameDetail.brand || 'N/A'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs font-medium text-gray-500 mb-1">Name</p>
                          <p className="text-sm font-semibold text-gray-900">{frameDetail.name || 'N/A'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs font-medium text-gray-500 mb-1">Lenses Kind</p>
                          <p className="text-sm font-semibold text-gray-900">{frameDetail.lenses_kind || 'N/A'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs font-medium text-gray-500 mb-1">Lenses Color</p>
                          <p className="text-sm font-semibold text-gray-900">{frameDetail.lenses_color || 'N/A'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs font-medium text-gray-500 mb-1">Lens Count</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {frameDetail.lens_count !== null && frameDetail.lens_count !== undefined ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 text-indigo-800 rounded-full text-xs font-medium">
                                {frameDetail.lens_count}
                              </span>
                            ) : (
                              'N/A'
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Images */}
                      {(frameDetail.original_image_url || frameDetail.upscaled_image_url || frameDetail.final_image_url) && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {frameDetail.original_image_url && (
                            <div className="group">
                              <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                                <ImageIcon className="w-3 h-3" />
                                Original Image
                              </p>
                              <img
                                src={frameDetail.original_image_url}
                                alt="Original"
                                className="w-full rounded-lg border-2 border-gray-300 shadow-sm hover:shadow-md transition-shadow"
                              />
                            </div>
                          )}
                          {frameDetail.upscaled_image_url && (
                            <div className="group">
                              <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                                <ImageIcon className="w-3 h-3" />
                                Upscaled Image
                              </p>
                              <img
                                src={frameDetail.upscaled_image_url}
                                alt="Upscaled"
                                className="w-full rounded-lg border-2 border-blue-300 shadow-sm hover:shadow-md transition-shadow"
                              />
                            </div>
                          )}
                          {frameDetail.final_image_url && (
                            <div className="group">
                              <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                                <ImageIcon className="w-3 h-3" />
                                Final Image
                              </p>
                              <img
                                src={frameDetail.final_image_url}
                                alt="Final"
                                className="w-full rounded-lg border-2 border-green-400 shadow-lg hover:shadow-xl transition-shadow"
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Jobs Section */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <Workflow className="w-5 h-5 text-indigo-600" />
                        Processing Jobs
                        <span className="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">
                          {frameDetail.jobs?.length || 0}
                        </span>
                      </h3>
                    </div>

                    {frameDetail.jobs && frameDetail.jobs.length > 0 ? (
                      <div className="divide-y divide-gray-200">
                        {frameDetail.jobs.map((job) => {
                          const isExpanded = expandedJobs.has(job.id);
                          const progress = getOverallJobProgress(job);
                          
                          return (
                            <div key={job.id} className="bg-white">
                              {/* Job Header */}
                              <div
                                className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                                onClick={() => toggleJob(job.id)}
                              >
                                <div className="flex items-center justify-between gap-4">
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <button className="flex-shrink-0 text-gray-400 hover:text-gray-600">
                                      {isExpanded ? (
                                        <ChevronDown className="w-5 h-5" />
                                      ) : (
                                        <ChevronRight className="w-5 h-5" />
                                      )}
                                    </button>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-3 mb-2">
                                        <h4 className="font-semibold text-gray-900 truncate">{job.name}</h4>
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(job.status)}`}>
                                          {getStatusIcon(job.status)}
                                          {job.status}
                                        </span>
                                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                          {job.kind}
                                        </span>
                                      </div>
                                      <ProgressBar
                                        completed={progress.completed}
                                        total={progress.total}
                                        status={job.status}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Workflows (Expanded) */}
                              {isExpanded && job.workflows && job.workflows.length > 0 && (
                                <div className="px-4 pb-4 pl-12 space-y-2">
                                  {job.workflows.map((workflow) => {
                                    const workflowProgress = calculateProgress(workflow.nodes);
                                    
                                    return (
                                      <div
                                        key={workflow.id}
                                        className={`bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border-2 transition-all cursor-pointer hover:shadow-md ${
                                          selectedWorkflowForViz === workflow.id
                                            ? 'border-indigo-600 shadow-lg'
                                            : 'border-indigo-200 hover:border-indigo-400'
                                        }`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedWorkflowForViz(workflow.id);
                                        }}
                                      >
                                        <div className="p-3">
                                          <div className="flex items-center gap-3">
                                            <div className="flex-1">
                                              <div className="flex items-center gap-2 mb-2">
                                                <Workflow className="w-4 h-4 text-indigo-600" />
                                                <span className="font-medium text-gray-900 text-sm">
                                                  {workflow.name}
                                                </span>
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(workflow.status)}`}>
                                                  {getStatusIcon(workflow.status)}
                                                  {workflow.status}
                                                </span>
                                                {selectedWorkflowForViz === workflow.id && (
                                                  <span className="ml-auto text-xs px-2 py-1 rounded bg-indigo-600 text-white flex items-center gap-1">
                                                    <GitBranch className="w-3 h-3" />
                                                    Visualizing
                                                  </span>
                                                )}
                                              </div>
                                              <ProgressBar
                                                completed={workflowProgress.completed}
                                                total={workflowProgress.total}
                                                status={workflow.status}
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-12 text-center">
                        <Workflow className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 font-medium">No processing jobs found</p>
                        <p className="text-sm text-gray-400 mt-1">Jobs will appear here once processing begins</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Column - Workflow Visualization */}
                <div className="lg:col-span-1">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 sticky top-6" style={{ height: 'calc(100vh - 200px)' }}>
                    <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-4 py-3 border-b border-gray-200">
                      <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        <GitBranch className="w-4 h-4 text-indigo-600" />
                        Workflow Visualization
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">Click nodes for details</p>
                    </div>
                    
                    <div className="h-full pb-12">
                      {selectedWorkflowForViz ? (
                        <div className="border-t bg-gray-50 overflow-hidden h-full">
                          <ReactFlow
                            nodes={nodes}
                            edges={edges}
                            onNodesChange={onNodesChange}
                            onEdgesChange={onEdgesChange}
                            nodeTypes={nodeTypes}
                            fitView
                            proOptions={{ hideAttribution: true }}
                            minZoom={0.1}
                            maxZoom={1.5}
                          >
                            <Controls className="bg-white" />
                            <MiniMap 
                              nodeColor={(node) => {
                                const status = node.data.status;
                                if (status === 'completed') return '#10b981';
                                if (status === 'running') return '#3b82f6';
                                if (status === 'needs_update') return '#f59e0b';
                                if (status === 'failed') return '#ef4444';
                                return '#9ca3af';
                              }}
                              className="bg-white border border-gray-200"
                              style={{ height: 80 }}
                            />
                            <Background variant="dots" gap={12} size={1} />
                          </ReactFlow>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-full text-gray-400">
                          <div className="text-center">
                            <GitBranch className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p className="text-sm font-medium">Click a workflow to visualize</p>
                            <p className="text-xs mt-1">Workflow dependency graph will appear here</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Node Detail Modal */}
      <WorkflowNodeDetailModal
        isOpen={nodeModalOpen}
        onClose={() => setNodeModalOpen(false)}
        nodeId={selectedNodeId}
      />
    </>
  );
}

function RevisionDialog({ isOpen, onClose, frameId }) {
  const { authenticatedFetch } = useAuth();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleTransparentLensError = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/revision/lenses/transparent_lens`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            glasses_frame_id: frameId
          })
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSuccess(true);
      
      setTimeout(() => {
        setConfirmDialogOpen(false);
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error submitting revision:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && confirmDialogOpen && !loading) {
      handleConfirm();
    }
  };

  useEffect(() => {
    if (confirmDialogOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [confirmDialogOpen, loading]);

  if (!isOpen) return null;

  return (
    <>
      {/* Main Revision Dialog */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-4 flex items-center justify-between rounded-t-lg">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6" />
              <h2 className="text-xl font-bold">Revisione</h2>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-lg p-1 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-gray-600 mb-4">Seleziona il tipo di revisione necessaria:</p>
            
            <button
              onClick={handleTransparentLensError}
              className="w-full px-4 py-3 bg-red-50 hover:bg-red-100 border-2 border-red-200 text-red-800 rounded-lg transition-colors text-left font-medium"
            >
              Errore nella creazione della lente trasparente
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {confirmDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
            {/* Header */}
            <div className="bg-yellow-500 text-white px-6 py-4 rounded-t-lg">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-6 h-6" />
                <h3 className="text-lg font-bold">Conferma Revisione</h3>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {!success && !error && (
                <>
                  <p className="text-gray-700 mb-4">
                    Sei sicuro di voler segnalare un errore nella creazione della lente trasparente per questo frame?
                  </p>
                  <p className="text-sm text-gray-500 mb-6">
                    Premi <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs font-mono">Enter</kbd> per confermare
                  </p>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setConfirmDialogOpen(false)}
                      disabled={loading}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
                    >
                      Annulla
                    </button>
                    <button
                      onClick={handleConfirm}
                      disabled={loading}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Invio...
                        </>
                      ) : (
                        'Conferma'
                      )}
                    </button>
                  </div>
                </>
              )}

              {success && (
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <p className="text-green-800 font-medium">Revisione inviata con successo!</p>
                </div>
              )}

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-red-800">Errore</p>
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setError(null);
                      setConfirmDialogOpen(false);
                    }}
                    className="mt-4 w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Chiudi
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function DeleteFrameDialog({ isOpen, onClose, frameId, onDeleteSuccess }) {
  const { authenticatedFetch } = useAuth();
  
  const [step, setStep] = useState('loading'); // loading, select, confirm, deleting, success, error
  const [materials, setMaterials] = useState(null);
  const [deleteMode, setDeleteMode] = useState(null); // 'workflow' or 'all'
  const [confirmationCode, setConfirmationCode] = useState('');
  const [requiredCode, setRequiredCode] = useState('');
  const [error, setError] = useState(null);
  const [deleteResult, setDeleteResult] = useState(null);

  // Generate random 5-letter code
  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let code = '';
    for (let i = 0; i < 5; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  // Fetch materials when dialog opens
  useEffect(() => {
    if (isOpen && frameId) {
      setStep('loading');
      setError(null);
      fetchMaterials();
    }
  }, [isOpen, frameId]);

  const fetchMaterials = async () => {
    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/glasses-frames/${frameId}/materials`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch materials: ${response.status}`);
      }

      const data = await response.json();
      setMaterials(data);
      setStep('select');
    } catch (err) {
      console.error('Error fetching materials:', err);
      setError(err.message);
      setStep('error');
    }
  };

  const handleDeleteModeSelect = (mode) => {
    setDeleteMode(mode);
    setRequiredCode(generateCode());
    setConfirmationCode('');
    setStep('confirm');
  };

  const handleConfirmDelete = async () => {
    if (confirmationCode !== requiredCode) {
      return;
    }

    setStep('deleting');
    setError(null);

    try {
      const deleteFiles = deleteMode === 'all';
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/glasses-frames/${frameId}?delete_files=${deleteFiles}`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to delete: ${response.status}`);
      }

      const result = await response.json();
      setDeleteResult(result);
      
      if (result.success) {
        setStep('success');
        setTimeout(() => {
          onDeleteSuccess?.();
          handleClose();
        }, 2000);
      } else {
        setStep('error');
        setError(result.message);
      }
    } catch (err) {
      console.error('Error deleting frame:', err);
      setError(err.message);
      setStep('error');
    }
  };

  const handleClose = () => {
    setStep('loading');
    setMaterials(null);
    setDeleteMode(null);
    setConfirmationCode('');
    setRequiredCode('');
    setError(null);
    setDeleteResult(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-red-600 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Trash2 className="w-6 h-6" />
            <h2 className="text-xl font-bold">Delete Glasses Frame</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-white hover:bg-red-700 rounded-lg p-1 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Loading State */}
          {step === 'loading' && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mb-4" />
              <p className="text-gray-600">Loading frame data...</p>
            </div>
          )}

          {/* Select Deletion Mode */}
          {step === 'select' && materials && (
            <div>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Frame Data Summary</h3>
                <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Jobs:</span>
                    <span className="font-medium text-gray-900">{materials.summary.jobs}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Workflows:</span>
                    <span className="font-medium text-gray-900">{materials.summary.workflows}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Workflow Nodes:</span>
                    <span className="font-medium text-gray-900">{materials.summary.workflow_nodes}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Images:</span>
                    <span className="font-medium text-gray-900">{materials.summary.images}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Files:</span>
                    <span className="font-medium text-gray-900">{materials.summary.files}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Labels:</span>
                    <span className="font-medium text-gray-900">{materials.summary.labels}</span>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Deletion Mode</h3>
                
                {/* Option 1: Delete Workflow Data Only */}
                <div
                  onClick={() => handleDeleteModeSelect('workflow')}
                  className="border-2 border-gray-300 rounded-lg p-4 mb-3 cursor-pointer hover:border-orange-500 hover:bg-orange-50 transition-all"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full border-2 border-gray-400 mt-0.5"></div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1">Delete Workflow Data Only</h4>
                      <p className="text-sm text-gray-600 mb-2">
                        Removes jobs and workflow data while preserving images and files
                      </p>
                      <div className="text-xs text-gray-500">
                        <p>Will delete:</p>
                        <ul className="list-disc list-inside ml-2 mt-1">
                          <li>Workflow Nodes Locks</li>
                          <li>Workflow Node Dependencies</li>
                          <li>Workflow Dependencies</li>
                          <li>Workflow Nodes</li>
                          <li>Workflows</li>
                          <li>Jobs</li>
                          <li>Glasses Frame Record</li>
                        </ul>
                        <p className="mt-2 font-medium text-orange-600">Images and files will be preserved</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Option 2: Delete Everything */}
                <div
                  onClick={() => handleDeleteModeSelect('all')}
                  className="border-2 border-red-300 rounded-lg p-4 cursor-pointer hover:border-red-600 hover:bg-red-50 transition-all"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full border-2 border-red-400 mt-0.5"></div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1">Delete Everything</h4>
                      <p className="text-sm text-gray-600 mb-2">
                        Permanently removes all data including images and storage files
                      </p>
                      <div className="text-xs text-gray-500">
                        <p>Will delete:</p>
                        <ul className="list-disc list-inside ml-2 mt-1">
                          <li>All workflow data (from above)</li>
                          <li>Labels and Label Jobs</li>
                          <li>Images (database records)</li>
                          <li>Files (database records)</li>
                          <li>Storage Files (from buckets)</li>
                        </ul>
                        <p className="mt-2 font-medium text-red-600">⚠️ This action is irreversible!</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Confirmation Step */}
          {step === 'confirm' && (
            <div>
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-yellow-900 mb-1">Confirm Deletion</h3>
                    <p className="text-sm text-yellow-800">
                      {deleteMode === 'workflow' 
                        ? 'You are about to delete workflow data for this frame.'
                        : 'You are about to permanently delete ALL data for this frame, including images and files.'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type <span className="font-mono font-bold text-lg text-red-600">{requiredCode}</span> to confirm
                </label>
                <input
                  type="text"
                  value={confirmationCode}
                  onChange={(e) => setConfirmationCode(e.target.value.toUpperCase())}
                  placeholder="Enter confirmation code"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 font-mono text-lg tracking-wider"
                  autoFocus
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep('select')}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Back
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={confirmationCode !== requiredCode}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                    confirmationCode === requiredCode
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {deleteMode === 'workflow' ? 'Delete Workflow Data' : 'Delete Everything'}
                </button>
              </div>
            </div>
          )}

          {/* Deleting State */}
          {step === 'deleting' && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 text-red-600 animate-spin mb-4" />
              <p className="text-gray-600 font-medium">Deleting data...</p>
              <p className="text-sm text-gray-500 mt-1">This may take a moment</p>
            </div>
          )}

          {/* Success State */}
          {step === 'success' && deleteResult && (
            <div>
              <div className="flex flex-col items-center justify-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Deletion Complete</h3>
                <p className="text-gray-600 text-center mb-4">{deleteResult.message}</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Deleted Items:</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {Object.entries(deleteResult.deleted_counts).map(([key, count]) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}:</span>
                      <span className="font-medium text-gray-900">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Error State */}
          {step === 'error' && (
            <div>
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-red-900 mb-1">Error</h3>
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep('select')}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Back
                </button>
                <button
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function GlassesFramesTable() {
  const { authenticatedFetch, hasRole } = useAuth();
  
  const [frames, setFrames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [frameToRevise, setFrameToRevise] = useState(null);
  const [selectedFrameId, setSelectedFrameId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [softboxOpen, setSoftboxOpen] = useState(false);
  const [softboxImage, setSoftboxImage] = useState(null);
  
  // State variables for delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [frameToDelete, setFrameToDelete] = useState(null);
  
  // State variables for delete all functionality
  const [deleteAllLoading, setDeleteAllLoading] = useState(false);
  const [deleteAllError, setDeleteAllError] = useState(null);

  // NEW: Search and sort state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const observerTarget = useRef(null);
  const pageSize = 20;

  const fetchFrames = useCallback(async (page) => {
    if (loading) return;
    
    setLoading(true);
    setError(null);

    try {
      // Build query params
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }

      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/glasses-frames/?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (page === 1) {
        setFrames(data.data);
      } else {
        setFrames(prev => [...prev, ...data.data]);
      }
      
      setTotalCount(data.total);
      setHasMore(page < data.total_pages);
      setCurrentPage(page);
      
    } catch (err) {
      console.error('Error fetching frames:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, loading, searchQuery, sortBy, sortOrder]);

  // Initial load
  useEffect(() => {
    fetchFrames(1);
  }, [searchQuery, sortBy, sortOrder]); // Re-fetch when search or sort changes

  // Infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchFrames(currentPage + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, loading, currentPage, fetchFrames]);

  const handleRefresh = () => {
    setFrames([]);
    setCurrentPage(1);
    setHasMore(true);
    fetchFrames(1);
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
    setFrames([]);
    setCurrentPage(1);
    setHasMore(true);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setFrames([]);
    setCurrentPage(1);
    setHasMore(true);
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      // Toggle order if clicking same field
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default desc order
      setSortBy(field);
      setSortOrder('desc');
    }
    setFrames([]);
    setCurrentPage(1);
    setHasMore(true);
  };

  const handleViewDetails = (frameId) => {
    setSelectedFrameId(frameId);
    setModalOpen(true);
  };

  const handleImageClick = (imageUrl, label) => {
    setSoftboxImage({ url: imageUrl, title: label });
    setSoftboxOpen(true);
  };

  const handleDeleteClick = (frameId, e) => {
    e.stopPropagation();
    setFrameToDelete(frameId);
    setDeleteDialogOpen(true);
  };
  const handleRevisionClick = (frameId, e) => {
    e.stopPropagation();
    setFrameToRevise(frameId);
    setRevisionDialogOpen(true);
  };
  const handleDeleteSuccess = () => {
    handleRefresh();
  };

  const handleDeleteAll = async () => {
    const confirmed = window.confirm(
      '⚠️ WARNING: This will delete ALL glasses frames and their associated data!\n\n' +
      'This action cannot be undone.\n\n' +
      'Type "DELETE ALL" in the next prompt to confirm.'
    );
    
    if (!confirmed) return;

    const confirmText = window.prompt('Type "DELETE ALL" to confirm deletion of all records:');
    
    if (confirmText !== 'DELETE ALL') {
      alert('Confirmation text did not match. Deletion cancelled.');
      return;
    }

    setDeleteAllLoading(true);
    setDeleteAllError(null);

    try {
      const deletePromises = frames.map(frame => 
        authenticatedFetch(
          `${config.BACKEND_API_ADDRESS}/glasses-frames/${frame.id}?delete_files=true`,
          { method: 'DELETE' }
        )
      );

      await Promise.all(deletePromises);
      
      alert(`Successfully deleted ${frames.length} frames!`);
      handleRefresh();
    } catch (err) {
      console.error('Error deleting all frames:', err);
      setDeleteAllError(err.message);
      alert(`Error deleting frames: ${err.message}`);
    } finally {
      setDeleteAllLoading(false);
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? '↑' : '↓';
  };
  
  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Glasses Frames</h1>
              <p className="text-sm text-gray-600 mt-1">
                {totalCount} total frames
                {searchQuery && <span className="ml-2 text-indigo-600">• Filtered</span>}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {hasRole('admin') && (
                <button
                  onClick={handleDeleteAll}
                  disabled={deleteAllLoading || frames.length === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Testing only - delete all records"
                >
                  <Trash2 className={`w-4 h-4 ${deleteAllLoading ? 'animate-pulse' : ''}`} />
                  <span className="text-sm font-medium">
                    {deleteAllLoading ? 'Deleting...' : 'Delete All (TEST)'}
                  </span>
                </button>
              )}
              
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="text-sm font-medium">Refresh</span>
              </button>
            </div>
          </div>

          {/* Search and Sort Controls */}
          <div className="flex items-center gap-4">
            {/* Search Bar */}
            <div className="flex-1 max-w-md relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search by name or ID..."
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                {searchQuery && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Sort Controls */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Sort by:</span>
              <div className="flex gap-2">
                <button
                  onClick={() => handleSortChange('created_at')}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    sortBy === 'created_at'
                      ? 'bg-indigo-100 text-indigo-700 font-medium'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Created {getSortIcon('created_at')}
                </button>
                <button
                  onClick={() => handleSortChange('updated_at')}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    sortBy === 'updated_at'
                      ? 'bg-indigo-100 text-indigo-700 font-medium'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Updated {getSortIcon('updated_at')}
                </button>
                <button
                  onClick={() => handleSortChange('name')}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    sortBy === 'name'
                      ? 'bg-indigo-100 text-indigo-700 font-medium'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Name {getSortIcon('name')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="px-6 py-6">
          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">Error loading frames</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Delete All Error Display */}
          {deleteAllError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">Error deleting all frames</p>
                <p className="text-sm text-red-700">{deleteAllError}</p>
              </div>
            </div>
          )}

          {/* Table */}
          {frames.length === 0 && !loading ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                  <Package className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {searchQuery ? 'No matching frames found' : 'No frames found'}
                </h3>
                <p className="text-gray-600">
                  {searchQuery ? 'Try adjusting your search query' : 'Start by adding some glasses frames'}
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Images</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div>Classification</div>
                        <div className="flex gap-4 mt-1 text-xs font-normal normal-case text-gray-400">
                          <span>Brand</span>
                          <span>Lens Type & Color</span>
                          <span>Frame Style & Color</span>
                          <span>Lens Count</span>
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Version</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Jobs</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {frames.map((frame) => (
                      <tr 
                        key={frame.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            {frame.original_image_url ? (
                              <div 
                                className="relative cursor-pointer group"
                                onClick={() => handleImageClick(frame.original_image_url, 'Original')}
                              >
                                <img
                                  src={frame.original_image_url}
                                  alt="Original"
                                  className="w-16 h-16 object-contain rounded-lg border border-gray-300 group-hover:border-indigo-500 transition-colors"
                                />
                                <span className="absolute -bottom-1 -right-1 bg-gray-600 text-white text-xs px-1 rounded">Orig</span>
                              </div>
                            ) : (
                              <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center relative">
                                <Eye className="w-4 h-4 text-gray-400" />
                                <span className="absolute -bottom-1 -right-1 bg-gray-600 text-white text-xs px-1 rounded">Orig</span>
                              </div>
                            )}
                            
                            {frame.upscaled_image_url ? (
                              <div 
                                className="relative cursor-pointer group"
                                onClick={() => handleImageClick(frame.upscaled_image_url, 'Upscaled')}
                              >
                                <img
                                  src={frame.upscaled_image_url}
                                  alt="Upscaled"
                                  className="w-16 h-16 object-contain rounded-lg border border-blue-300 group-hover:border-blue-500 transition-colors"
                                />
                                <span className="absolute -bottom-1 -right-1 bg-blue-600 text-white text-xs px-1 rounded">Up</span>
                              </div>
                            ) : (
                              <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center relative">
                                <Eye className="w-4 h-4 text-gray-400" />
                                <span className="absolute -bottom-1 -right-1 bg-blue-600 text-white text-xs px-1 rounded">Up</span>
                              </div>
                            )}
                            
                            {frame.final_image_url ? (
                              <div 
                                className="relative cursor-pointer group"
                                onClick={() => handleImageClick(frame.final_image_url, 'Final')}
                              >
                                <img
                                  src={frame.final_image_url}
                                  alt="Final"
                                  className="w-16 h-16 object-contain rounded-lg border border-green-400 shadow-md group-hover:border-green-600 transition-colors"
                                />
                                <span className="absolute -bottom-1 -right-1 bg-green-600 text-white text-xs px-1 rounded">Final</span>
                              </div>
                            ) : (
                              <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center relative">
                                <Eye className="w-4 h-4 text-gray-400" />
                                <span className="absolute -bottom-1 -right-1 bg-green-600 text-white text-xs px-1 rounded">Final</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {frame.name || 'N/A'}
                        </td>

                        <td className="px-6 py-4">
                          <div className="flex gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium text-gray-500 mb-1">Brand</div>
                              <div className="text-sm text-gray-900">
                                {frame.brand || <span className="text-gray-400">N/A</span>}
                              </div>
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium text-gray-500 mb-1">Lens</div>
                              <div className="flex flex-col gap-0.5">
                                {frame.lens_type && (
                                  <span className="text-xs text-gray-900">{frame.lens_type}</span>
                                )}
                                {frame.lens_color && (
                                  <span className="text-xs text-gray-600">{frame.lens_color}</span>
                                )}
                                {!frame.lens_type && !frame.lens_color && (
                                  <span className="text-xs text-gray-400">N/A</span>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium text-gray-500 mb-1">Frame</div>
                              <div className="flex flex-col gap-0.5">
                                {frame.frame_style && (
                                  <span className="text-xs text-gray-900">{frame.frame_style}</span>
                                )}
                                {frame.frame_color && (
                                  <span className="text-xs text-gray-600">{frame.frame_color}</span>
                                )}
                                {!frame.frame_style && !frame.frame_color && (
                                  <span className="text-xs text-gray-400">N/A</span>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium text-gray-500 mb-1">Lens Count</div>
                              <div className="text-sm text-gray-900">
                                {frame.lens_count !== null && frame.lens_count !== undefined ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 text-indigo-800 rounded-full text-xs font-medium">
                                    {frame.lens_count} {frame.lens_count === 1 ? 'Lens' : 'Lenses'}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">N/A</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>


                        <td className="px-6 py-4 text-sm text-gray-900">
                          {frame.version || 'N/A'}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                              Total: {frame.total_jobs}
                            </span>
                            {frame.pending_jobs > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                                <Clock className="w-3 h-3" />
                                {frame.pending_jobs}
                              </span>
                            )}
                            {frame.running_jobs > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                <RefreshCw className="w-3 h-3" />
                                {frame.running_jobs}
                              </span>
                            )}
                            {frame.completed_jobs > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                                <CheckCircle className="w-3 h-3" />
                                {frame.completed_jobs}
                              </span>
                            )}
                            {frame.failed_jobs > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                                <XCircle className="w-3 h-3" />
                                {frame.failed_jobs}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {new Date(frame.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleViewDetails(frame.id)}
                              className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-xs font-medium"
                            >
                              More Info
                              <ChevronRight className="w-3 h-3" />
                            </button>
                            <button
                              onClick={(e) => handleRevisionClick(frame.id, e)}
                              className="flex items-center gap-2 px-3 py-1.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-xs font-medium"
                              title="Revisione"
                            >
                              <AlertTriangle className="w-3 h-3" />
                            </button>
                            {hasRole('admin') && (
                              <button
                                onClick={(e) => handleDeleteClick(frame.id, e)}
                                className="flex items-center gap-2 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs font-medium"
                                title="Delete frame"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Loading indicator for infinite scroll */}
              <div ref={observerTarget} className="px-6 py-4 text-center">
                {loading && (
                  <div className="flex items-center justify-center gap-2">
                    <RefreshCw className="w-5 h-5 text-indigo-600 animate-spin" />
                    <span className="text-sm text-gray-600">Loading more...</span>
                  </div>
                )}
                {!hasMore && frames.length > 0 && (
                  <span className="text-sm text-gray-500">No more frames to load</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      <GlassesFrameDetailModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        frameId={selectedFrameId}
      />

      {/* Delete Dialog */}
      <DeleteFrameDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        frameId={frameToDelete}
        onDeleteSuccess={handleDeleteSuccess}
      />
      {/* Revision Dialog */}
      <RevisionDialog
        isOpen={revisionDialogOpen}
        onClose={() => setRevisionDialogOpen(false)}
        frameId={frameToRevise}
      />
      {/* Softbox */}
      <Softbox
        isOpen={softboxOpen}
        onClose={() => setSoftboxOpen(false)}
        imageUrl={softboxImage?.url}
        title={softboxImage?.title}
      />
    </div>
  );
}

export default GlassesFramesTable;
